export type TLocalizedAuthorText = [string, string];

export interface IReportCollaborator {
  open_id: string;
  creator_name?: string;
  creator_name_en?: string;
  creator_head_picture?: string;
  creator_department?: string;
  creator_department_en?: string;
  email?: string;
}

export interface IReportAuthorSource {
  creator_name?: string;
  creator_name_en?: string;
  creator_head_picture?: string;
  creator_department?: string;
  creator_department_en?: string;
  email?: string;
  collaborators?: IReportCollaborator[] | null;
}

export interface IReportAuthorViewModel {
  key: string;
  identifier: string;
  name: TLocalizedAuthorText;
  department: TLocalizedAuthorText;
  avatar: string;
  email: string;
  isDatasourceAvatar?: boolean;
}

interface IBuildReportAuthorsOptions {
  textOrder?: 'cn-en' | 'en-cn';
  transformAvatar?: (avatar: string) => string;
}

const toLocalizedText = (textCN = '', textEN = '', textOrder: IBuildReportAuthorsOptions['textOrder']): TLocalizedAuthorText => {
  const cn = textCN || textEN;
  const en = textEN || textCN;
  return textOrder === 'en-cn' ? [en, cn] : [cn, en];
};

const hasPrimaryAuthor = (report: IReportAuthorSource): boolean => Boolean(
  report.creator_name || report.creator_name_en || report.creator_head_picture
  || report.creator_department || report.creator_department_en || report.email,
);

export const buildReportAuthors = (report: IReportAuthorSource, options: IBuildReportAuthorsOptions = {}): IReportAuthorViewModel[] => {
  const { textOrder = 'cn-en', transformAvatar = (avatar: string) => avatar } = options;
  const authors: IReportAuthorViewModel[] = [];
  if (hasPrimaryAuthor(report)) {
    authors.push({
      key: 'primary', identifier: '', name: toLocalizedText(report.creator_name, report.creator_name_en, textOrder),
      department: toLocalizedText(report.creator_department, report.creator_department_en, textOrder),
      avatar: transformAvatar(report.creator_head_picture || ''), email: report.email || '',
    });
  }
  (Array.isArray(report.collaborators) ? report.collaborators.slice(0, 2) : []).forEach((collaborator, index) => {
    authors.push({
      key: `collaborator-${index}-${collaborator.open_id || 'unknown'}`, identifier: collaborator.open_id || '',
      name: toLocalizedText(collaborator.creator_name, collaborator.creator_name_en, textOrder),
      department: toLocalizedText(collaborator.creator_department, collaborator.creator_department_en, textOrder),
      avatar: transformAvatar(collaborator.creator_head_picture || ''), email: collaborator.email || '',
    });
  });
  return authors;
};

export const getAuthorDisplayName = (author: IReportAuthorViewModel, lang: number): string => author.identifier || author.name[lang] || author.name[0] || author.name[1] || '';
export const getAuthorDepartment = (author: IReportAuthorViewModel, lang: number): string => author.department[lang] || author.department[0] || author.department[1] || '';

export const buildDatasourceAuthor = (name: string, avatar: string): IReportAuthorViewModel => ({
  key: `datasource-${name}`, identifier: '', name: [name, name], department: ['', ''], avatar, email: '', isDatasourceAvatar: Boolean(avatar),
});
